import React from 'react'
import { useOutletContext } from 'react-router-dom'
import {
  Card,
  Typography,
  Table,
  Select,
  InputNumber,
  Button,
  Flex,
} from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import {
  ourEvents,
  fbEvents,
  fbActionSources,
  pixelPlatformOptions,
  type EventMapping,
} from '@/data'
import type { PixelEditorContext } from '../../index'

const { Title, Text } = Typography

export const EventsPage: React.FC = () => {
  const { platform, event_mappings, setEventMappings } =
    useOutletContext<PixelEditorContext>()

  const platformLabel =
    pixelPlatformOptions.find((o) => o.value === platform)?.label ?? platform

  const updateMapping = (index: number, updates: Partial<EventMapping>) => {
    setEventMappings(
      event_mappings.map((m, i) => (i === index ? { ...m, ...updates } : m)),
    )
  }

  const addMapping = () => {
    setEventMappings([
      ...event_mappings,
      {
        inbound_event: ourEvents[0],
        outbound_event: fbEvents[0],
        value: 0,
      },
    ])
  }

  const removeMapping = (index: number) => {
    setEventMappings(event_mappings.filter((_, i) => i !== index))
  }

  const columns = [
    {
      title: 'Our Event',
      dataIndex: 'inbound_event',
      key: 'inbound_event',
      width: 180,
      render: (value: string, _: EventMapping, index: number) => (
        <Select
          value={value}
          onChange={(v) => updateMapping(index, { inbound_event: v })}
          style={{ width: '100%' }}
          options={ourEvents.map((e) => ({ value: e, label: e }))}
        />
      ),
    },
    {
      title: 'Platform Event Name',
      dataIndex: 'outbound_event',
      key: 'outbound_event',
      width: 180,
      render: (value: string, _: EventMapping, index: number) => (
        <Select
          value={value}
          onChange={(v) => updateMapping(index, { outbound_event: v })}
          style={{ width: '100%' }}
          options={fbEvents.map((e) => ({ value: e, label: e }))}
        />
      ),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: 120,
      render: (value: number, _: EventMapping, index: number) => (
        <InputNumber
          value={value}
          onChange={(v) => updateMapping(index, { value: v ?? 0 })}
          min={0}
          prefix="$"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Action Source',
      dataIndex: 'action_source',
      key: 'action_source',
      width: 180,
      render: (value: string | undefined, _: EventMapping, index: number) => (
        <Select
          value={value || undefined}
          onChange={(v) => updateMapping(index, { action_source: v })}
          allowClear
          placeholder="Select..."
          style={{ width: '100%' }}
          options={fbActionSources.map((s) => ({ value: s, label: s }))}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: unknown, __: EventMapping, index: number) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeMapping(index)}
        />
      ),
    },
  ]

  return (
    <div>
      <Title level={4}>Event Mappings ({platformLabel})</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Map your internal events to platform pixel events.
      </Text>

      <Card>
        <Table
          dataSource={event_mappings}
          columns={columns}
          pagination={false}
          size="small"
          rowKey={(_, index) => String(index)}
        />
        <Flex style={{ marginTop: 16 }}>
          <Button type="dashed" icon={<PlusOutlined />} onClick={addMapping}>
            Add Event Mapping
          </Button>
        </Flex>
      </Card>
    </div>
  )
}
